import { useState, type FormEventHandler, type ReactNode } from "react";

export type FormErrors<T> = Partial<Record<keyof T, string | null>>;

interface FormInputEvent<T> {
  target: {
    name: keyof T | string;
    value: unknown;
  };
}

export function useForm<T extends object>(
  initialFValues: T,
  validateOnChange = false,
  validate?: (fieldValues: Partial<T>) => void,
) {
  const [values, setValues] = useState<T>(initialFValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});

  const handleInputChange = (event: FormInputEvent<T>) => {
    const { name, value } = event.target;
    const fieldName = name as keyof T;
    const changedValues = { [fieldName]: value } as Partial<T>;

    setValues((previous) => ({ ...previous, ...changedValues }));
    if (validateOnChange) validate?.(changedValues);
  };

  const handleMultipleInputChange = (...changedValues: Array<Partial<T> | undefined>) => {
    setValues((previous) => Object.assign({}, previous, ...changedValues.filter(Boolean)));
  };

  const resetForm = () => {
    setValues(initialFValues);
    setErrors({});
  };

  return {
    values,
    setValues,
    errors,
    setErrors,
    handleInputChange,
    handleMultipleInputChange,
    resetForm,
  };
}

interface FormProps {
  classes?: string;
  children?: ReactNode;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  [key: string]: unknown;
}

export function Form({ classes, children, ...other }: FormProps) {
  return (
    <form
      className={classes ?? "h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"}
      {...other}
    >
      {children}
    </form>
  );
}
